/**
 * Label payloads — PURE domain functions shared by every label surface.
 *
 * A lot label carries a QR pointing at the public per-lot traceability page. The
 * payload must be IDENTICAL wherever the label is produced — the operator's
 * thermal print from planta (ZPL) and the backoffice PDF are the same sticker for
 * the same lot — so the URL is built here rather than in either app.
 *
 * INVARIANT (CLAUDE.md): the lot number is server-authoritative. The QR must
 * encode the CANONICAL number, never a device provisional placeholder; the
 * caller gates on that before encoding.
 *
 * PURITY: string formatting only.
 */

/** Base URL of the public per-lot traceability page (the QR target host + path). */
export const TRACE_BASE_URL = 'https://obrava.app/t';

/**
 * Build the QR payload (public traceability URL) for a lot number — an internal
 * MP lot from a reception, or a finished lot from an OT close.
 *
 * @param lotNumber the CANONICAL lot number (e.g. 'MP-L-00042').
 * @returns e.g. 'https://obrava.app/t/MP-L-00042'.
 * @throws Error when `lotNumber` is empty.
 */
export function lotTraceUrl(lotNumber: string): string {
  if (!lotNumber || lotNumber.trim() === '') {
    throw new Error('lotTraceUrl: lotNumber is required');
  }
  return `${TRACE_BASE_URL}/${encodeURIComponent(lotNumber.trim())}`;
}

/** The host of an http(s) URL, lower-cased, or null when the text is not one. */
function hostOfUrl(text: string): string | null {
  const match = /^https?:\/\/([^/?#]+)/i.exec(text);
  return match?.[1] === undefined ? null : match[1].toLowerCase();
}

/** The host of Obrava's label QR (that of {@link TRACE_BASE_URL}). */
const TRACE_HOST = hostOfUrl(TRACE_BASE_URL);

/**
 * The lot numbers a SCANNED code could stand for, MOST specific first — the inverse of {@link lotTraceUrl}.
 *
 *  1. the trimmed text itself (a lot number typed or printed on a sheet), ALWAYS first;
 *  2. only when the text is Obrava's own traceability URL (`https://obrava.app/t/<lot>`, exactly two path segments, query and
 *     fragment ignored): the lot number percent-decoded, and the raw segment.
 *
 * A text that is not that URL yields nothing more: no «last piece after a slash» («L23/45» is NOT lot «45», «LOT#5» is NOT
 * lot «LOT»), and a URL of another host never stands for a lot. The caller matches the candidates EXACTLY, most specific
 * first, against the lot numbers it knows: a near-miss must fail loudly rather than silently pick a neighbouring lot.
 *
 * @returns the distinct candidates; empty when the code is blank.
 */
export function lotScanCandidates(scanned: string): string[] {
  const trimmed = scanned.trim();
  if (trimmed === '') return [];
  const out: string[] = [trimmed];
  if (TRACE_HOST !== null && hostOfUrl(trimmed) === TRACE_HOST) {
    const path = trimmed.replace(/^https?:\/\/[^/?#]+/i, '').split(/[?#]/)[0] ?? '';
    const segments = path.split('/').filter((part) => part !== '');
    const segment = segments.length === 2 && segments[0] === 't' ? segments[1] : undefined;
    if (segment !== undefined) {
      try {
        out.push(decodeURIComponent(segment));
      } catch {
        // A malformed escape sequence just means this candidate is not usable.
      }
      out.push(segment);
    }
  }
  return [...new Set(out)];
}
